import React, { Component, memo } from 'react';
import flvjs from './flv.min';
import PropTypes from 'prop-types';
import './index.css';

class CustomReactFlvPlayer extends Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();
    this.flvPlayerRef = element => {
      this.flvPlayerRef = element;
    };
    this.flvPlayer = null;
    this.DELAY_TIME_FOR_MESSAGE = 15;
    this.state = {
      delay: 0,
      isPaused: true,
    };
    this.checkInterval = null;
  }

  componentDidMount() {
    const { type, url, isLive, enableStashBuffer, stashInitialSize, hasAudio, hasVideo, handleError, enableWarning } =
      this.props;

    if (flvjs.isSupported()) {
      this.flvPlayer = flvjs.createPlayer(
        {
          type,
          isLive,
          url,
          hasAudio,
          hasVideo,
        },
        {
          enableStashBuffer,
          stashInitialSize,
        }
      );

      const checkDelay = () => {
        if (!this.flvPlayer) return;

        const buffered = this.flvPlayer.buffered;
        const currentTime = this.flvPlayer.currentTime;

        if (buffered.length > 0) {
          const bufferEnd = buffered.end(buffered.length - 1);
          const currentDelay = bufferEnd - currentTime;

          this.setState({ delay: currentDelay });
        }
      };

      flvjs.LoggingControl.enableError = false;
      flvjs.LoggingControl.enableWarn = enableWarning;
      this.flvPlayer.attachMediaElement(this.myRef.current);
      this.flvPlayer.load();

      this.myRef.current.addEventListener('play', this.handlePlay);
      this.myRef.current.addEventListener('pause', this.handlePause);

      this.flvPlayer.play().catch(error => {
        console.warn(`Error in video player: ${error}`);
        this.setState({ isPaused: true });
      });

      this.checkInterval = setInterval(checkDelay, 1000);
      this.flvPlayer.on(flvjs.Events.METADATA_ARRIVED, checkDelay);
      this.flvPlayer.on(flvjs.Events.STATISTICS_INFO, checkDelay);
      this.flvPlayer.on('error', err => {
        handleError(err);
        this.setState({ isPaused: true });
      });
    }
  }

  componentWillUnmount() {
    if (this.flvPlayer) this.flvPlayer.destroy();
    if (this.checkInterval) clearInterval(this.checkInterval);

    if (this.myRef.current) {
      this.myRef.current.removeEventListener('play', this.handlePlay);
      this.myRef.current.removeEventListener('pause', this.handlePause);
    }
  }

  handlePlay = () => {
    this.setState({ isPaused: false });
  };

  handlePause = () => {
    this.setState({ isPaused: true });
  };

  handlePlaceholderClick = () => {
    if (this.myRef.current) {
      this.myRef.current.play().catch(error => {
        console.warn('Error trying to play:', error);
      });
    }
  };

  render() {
    const { height, width, isMuted, showControls } = this.props;
    const { delay, isPaused } = this.state;

    return (
      <div style={{ width: width, height: height, position: 'relative' }}>
        {delay > this.DELAY_TIME_FOR_MESSAGE && (
          <div className='streamDelayNotify'>Внимание! Задержка более 15 секунд, обновите страницу</div>
        )}
        <video controls={showControls} muted={isMuted} ref={this.myRef} className='video' playsInline />
        {isPaused && (
          <div className='video-placeholder' onClick={this.handlePlaceholderClick}>
            <div>Видео на паузе. Нажмите для воспроизведения</div>
          </div>
        )}
      </div>
    );
  }
}

const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.url === nextProps.url &&
    prevProps.type === nextProps.type &&
    prevProps.isLive === nextProps.isLive &&
    prevProps.showControls === nextProps.showControls &&
    prevProps.hasAudio === nextProps.hasAudio &&
    prevProps.hasVideo === nextProps.hasVideo &&
    prevProps.enableStashBuffer === nextProps.enableStashBuffer &&
    prevProps.stashInitialSize === nextProps.stashInitialSize &&
    prevProps.height === nextProps.height &&
    prevProps.width === nextProps.width &&
    prevProps.isMuted === nextProps.isMuted &&
    prevProps.enableWarning === nextProps.enableWarning
  );
};

CustomReactFlvPlayer.propTypes = {
  type: PropTypes.string,
  url: PropTypes.string.isRequired,
  isLive: PropTypes.bool,
  showControls: PropTypes.bool,
  hasAudio: PropTypes.bool,
  hasVideo: PropTypes.bool,
  enableStashBuffer: PropTypes.bool,
  stashInitialSize: PropTypes.number,
  height: PropTypes.string,
  width: PropTypes.string,
  isMuted: PropTypes.bool,
  enableWarning: PropTypes.bool,
  enableError: PropTypes.bool,
  handleError: PropTypes.func,
};

CustomReactFlvPlayer.defaultProps = {
  type: 'flv',
  isLive: true,
  hasAudio: true,
  hasVideo: true,
  showControls: true,
  enableStashBuffer: true,
  stashInitialSize: 128,
  height: '100%',
  width: '100%',
  isMuted: false,
  handleError: err => {
    console.log(err);
  },
  enableWarning: false,
  enableError: false,
};

const MemoizedFlvPlayer = memo(CustomReactFlvPlayer, arePropsEqual);

export default MemoizedFlvPlayer;
